import { Button, Stack, Typography, useTheme } from "@mui/material";
import { MessageCircle } from "lucide-react";
import type { FC } from "react";
import { WhatIfCardViz } from "web_myplann/Goals/Components/WhatIf/WhatIfCardViz";
import { WhatIfExpandedCard } from "web_myplann/Goals/Components/WhatIf/WhatIfExpandedCard";
import { WhatIfOptionRow } from "web_myplann/Goals/Components/WhatIf/WhatIfOptionRow";
import { WhatIfSlider } from "web_myplann/Goals/Components/WhatIf/WhatIfSlider";
import { useExtraContributionPlan } from "web_myplann/Goals/Hooks/useExtraContributionPlan";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";

type Props = {
	goal: GoalDetail;
};

export const GoalDetailWhatIf: FC<Props> = ({ goal }) => {
	const theme = useTheme();
	const { t } = useResource();
	const {
		isVisible,
		extraAmount,
		setExtraAmount,
		activeScenario,
		setActiveScenario,
		scenarios,
		scenarioInfo,
		impact,
		savedLabel,
		openAdvisorChat,
	} = useExtraContributionPlan(goal);

	if (!isVisible) return null;

	const active = scenarioInfo[activeScenario];
	const inactive = scenarios.filter((scenario) => scenario !== activeScenario);

	return (
		<CollapsibleSection title={t(Resources.Goals.Detail.WhatIf.title)}>
			<Stack
				bgcolor={theme.palette.background.paper}
				px={1.6}
				py={1.6}
				gap={1.2}
			>
				<WhatIfSlider
					value={extraAmount}
					onChange={setExtraAmount}
					currencyCode={goal.currencyCode}
				/>

				{extraAmount > 0 ? (
					<Stack gap={0.6}>
						<WhatIfExpandedCard accent={active.accent} label={active.label}>
							<WhatIfCardViz
								scenario={activeScenario}
								impact={impact}
								savedLabel={savedLabel}
								currencyCode={goal.currencyCode}
							/>
						</WhatIfExpandedCard>

						{inactive.length > 0 && (
							<>
								<Typography
									variant="caption"
									fontWeight={600}
									color={theme.palette.grey[400]}
									textTransform="uppercase"
									letterSpacing={0.5}
									mt={0.6}
								>
									{t(Resources.Goals.Detail.WhatIf.otherOptions)}
								</Typography>
								{inactive.map((scenario) => {
									const info = scenarioInfo[scenario];
									const Icon = info.icon;
									return (
										<WhatIfOptionRow
											key={scenario}
											icon={
												<Icon size={theme.iconSize.sm} color={info.accent} />
											}
											accent={info.accent}
											label={info.label}
											subtitle={info.subtitle}
											onClick={() => setActiveScenario(scenario)}
										/>
									);
								})}
							</>
						)}

						<Button
							variant="primary"
							startIcon={<MessageCircle size={theme.iconSize.sm} />}
							onClick={openAdvisorChat}
							sx={{ mt: 1 }}
						>
							{t(Resources.Goals.Detail.WhatIf.talkToAdvisor)}
						</Button>
					</Stack>
				) : (
					<Typography
						variant="caption"
						color={theme.palette.grey[400]}
						textAlign="center"
						py={2}
					>
						{t(Resources.Goals.Detail.WhatIf.placeholder)}
					</Typography>
				)}
			</Stack>
		</CollapsibleSection>
	);
};
